import { useEffect, useRef, useState } from "react";
import {
  LayoutDashboard, Building2, CircleUserRound, LogOut, ChevronDown, Check,
  PanelLeftClose, PanelLeft, Menu, X, Network, Share2, Users2, ShieldCheck, Contact, Plus,
} from "lucide-react";
import { LogoLockup, BrandMark } from "@/components/Logo";
import { Modal, Field, TextInput, Select, Button } from "@/components/redvolt/kit";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export const ORG_NAV = [
  { key: "home", label: "Home", icon: LayoutDashboard },
  { key: "people", label: "People", icon: Contact },
  { key: "coordination", label: "Coordination", icon: Users2 },
  { key: "organization", label: "Organization", icon: Building2 },
];
const PLATFORM_NAV = [{ key: "admin", label: "Administration", icon: ShieldCheck }];

const useClickOutside = (onClose) => {
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [onClose]);
  return ref;
};

const NavList = ({ items, activeView, onNavigate, collapsed }) => (
  <nav className="flex flex-col gap-1" aria-label="Application">
    {items.map((item) => {
      const active = activeView === item.key;
      return (
        <button
          key={item.key}
          onClick={() => onNavigate(item.key)}
          data-testid={`nav-${item.key}`}
          aria-current={active ? "page" : undefined}
          title={collapsed ? item.label : undefined}
          className={cn(
            "group flex items-center gap-3 rounded-md py-2 text-sm transition-colors",
            collapsed ? "justify-center px-0" : "px-3",
            active
              ? "bg-accent font-semibold text-accent-foreground border-l-[3px] border-primary"
              : "font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 border-l-[3px] border-transparent"
          )}
        >
          <item.icon className={cn("h-[18px] w-[18px] shrink-0", active ? "text-primary" : "text-slate-400 group-hover:text-slate-600")} />
          {!collapsed && <span>{item.label}</span>}
        </button>
      );
    })}
  </nav>
);

const NavSections = ({ isPlatformAdmin, activeView, onNavigate, collapsed }) => (
  <div className="flex flex-col gap-6">
    <div>
      {!collapsed && (
        <p className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Organization</p>
      )}
      <NavList items={ORG_NAV} activeView={activeView} onNavigate={onNavigate} collapsed={collapsed} />
    </div>
    {isPlatformAdmin && (
      <div data-testid="platform-nav-section">
        {!collapsed && (
          <p className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Platform</p>
        )}
        {collapsed && <div className="mx-2 mb-2 h-px bg-slate-200" />}
        <NavList items={PLATFORM_NAV} activeView={activeView} onNavigate={onNavigate} collapsed={collapsed} />
      </div>
    )}
  </div>
);

const CreateOrgModal = ({ api, open, onClose, onCreateOrg }) => {
  const [orgTypes, setOrgTypes] = useState([]);
  const [name, setName] = useState("");
  const [orgType, setOrgType] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [county, setCounty] = useState("");
  const [line1, setLine1] = useState("");
  const [line2, setLine2] = useState("");
  const [zip, setZip] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setName(""); setOrgType(""); setState("");
    setCity(""); setCounty(""); setLine1(""); setLine2(""); setZip("");
    api.get("/org-types").then((r) => setOrgTypes(r.data.org_types || [])).catch(() => setOrgTypes([]));
  }, [open, api]);
  const submit = async () => {
    if (!name.trim() || !orgType || !state.trim()) { toast.error("Please complete all fields."); return; }
    setBusy(true);
    try {
      await onCreateOrg({
        name: name.trim(), org_type: orgType, state: state.trim(),
        city: city.trim() || null, county: county.trim() || null,
        address_line1: line1.trim() || null, address_line2: line2.trim() || null,
        postal_code: zip.trim() || null,
      });
      onClose();
    } catch (e) {
      const code = e?.response?.data?.detail;
      const msg = code === "org_creation_limit_reached"
        ? "You've reached the maximum number of organizations you can create."
        : code === "org_creation_not_authorized"
        ? "Your account isn't authorized to create organizations."
        : "Could not create the organization. Please try again.";
      toast.error(msg);
    } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Create organization"
      description="Start a new, independent organization. You'll become its Owner and switch into it right away."
      testid="create-org-modal"
      footer={<>
        <Button variant="ghost" onClick={onClose} data-testid="create-org-cancel">Cancel</Button>
        <Button onClick={submit} disabled={busy} data-testid="create-org-submit">
          {busy ? "Creating…" : "Create organization"}
        </Button>
      </>}>
      <div className="space-y-4">
        <Field label="Organization name">
          <TextInput value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Jefferson County Committee" data-testid="create-org-name" />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Organization type">
            <Select value={orgType} onChange={(e) => setOrgType(e.target.value)} data-testid="create-org-type">
              <option value="">Select a type…</option>
              {orgTypes.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </Select>
          </Field>
          <Field label="State">
            <TextInput value={state} onChange={(e) => setState(e.target.value)}
              placeholder="e.g. Louisiana" data-testid="create-org-state" />
          </Field>
        </div>
        <div className="border-t border-slate-100 pt-4">
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Location (optional)</p>
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Address line 1"><TextInput value={line1} onChange={(e) => setLine1(e.target.value)} placeholder="Street address" data-testid="create-org-line1" /></Field>
              <Field label="Address line 2"><TextInput value={line2} onChange={(e) => setLine2(e.target.value)} placeholder="Suite / unit" data-testid="create-org-line2" /></Field>
              <Field label="City / Town"><TextInput value={city} onChange={(e) => setCity(e.target.value)} data-testid="create-org-city" /></Field>
              <Field label="County / Parish"><TextInput value={county} onChange={(e) => setCounty(e.target.value)} data-testid="create-org-county" /></Field>
              <Field label="Postal / ZIP code"><TextInput value={zip} onChange={(e) => setZip(e.target.value)} data-testid="create-org-zip" /></Field>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const OrgSwitcher = ({ memberships, active, onSwitch, canCreate, atLimit, api, onCreateOrg }) => {
  const [open, setOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const ref = useClickOutside(() => setOpen(false));
  const multi = memberships.length > 1;
  const interactive = multi || canCreate;
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => interactive && setOpen((v) => !v)}
        data-testid="org-switcher"
        aria-haspopup={interactive ? "listbox" : undefined}
        aria-expanded={open}
        className={cn(
          "flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-left shadow-sm transition-colors",
          interactive ? "hover:border-slate-300 cursor-pointer" : "cursor-default"
        )}
      >
        <span className="grid h-8 w-8 place-items-center rounded-md bg-accent text-primary">
          <Building2 className="h-4 w-4" />
        </span>
        <span className="min-w-0">
          <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 leading-tight">Active Organization</span>
          <span data-testid="active-org-name" className="block max-w-[12rem] truncate text-sm font-semibold text-slate-900 leading-tight">
            {active?.tenant_name || "No organization"}
          </span>
        </span>
        {interactive && <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>
      {open && interactive && (
        <div
          role="listbox"
          data-testid="org-switcher-menu"
          className="absolute left-0 z-30 mt-1.5 w-72 rounded-lg border border-slate-200 bg-white p-1 shadow-lg"
        >
          {memberships.length > 0 && (
            <>
              <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Switch organization</p>
              {memberships.map((m) => {
                const sel = m.tenant_id === active?.tenant_id;
                return (
                  <button
                    key={m.tenant_id}
                    role="option"
                    aria-selected={sel}
                    data-testid={`org-option-${m.tenant_id}`}
                    onClick={() => { onSwitch(m.tenant_id); setOpen(false); }}
                    className={cn(
                      "flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors",
                      sel ? "bg-accent text-accent-foreground" : "text-slate-700 hover:bg-slate-100"
                    )}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <Building2 className={cn("h-4 w-4 shrink-0", sel ? "text-primary" : "text-slate-400")} />
                      <span className="truncate font-medium">{m.tenant_name}</span>
                    </span>
                    {sel && <Check className="h-4 w-4 text-primary shrink-0" />}
                  </button>
                );
              })}
            </>
          )}
          {canCreate && (
            <>
              {memberships.length > 0 && <div className="my-1 border-t border-slate-100" />}
              {atLimit ? (
                <p className="px-3 py-2 text-xs text-slate-400" data-testid="org-create-at-limit">
                  You've reached the maximum number of organizations you can create.
                </p>
              ) : (
                <button
                  data-testid="org-create-action"
                  onClick={() => { setShowCreate(true); setOpen(false); }}
                  className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-primary transition-colors hover:bg-accent"
                >
                  <Plus className="h-4 w-4 shrink-0" /> Create organization
                </button>
              )}
            </>
          )}
        </div>
      )}
      {canCreate && (
        <CreateOrgModal api={api} open={showCreate} onClose={() => setShowCreate(false)} onCreateOrg={onCreateOrg} />
      )}
    </div>
  );
};

const UserMenu = ({ me, activeView, onNavigate, onSignOut }) => {
  const [open, setOpen] = useState(false);
  const ref = useClickOutside(() => setOpen(false));
  const email = me.user.email || "Account";
  const display = me.user.name || email;
  const initial = display[0]?.toUpperCase() || "U";
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="user-menu"
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-lg px-1.5 py-1.5 transition-colors hover:bg-slate-100"
      >
        <span className="grid h-8 w-8 place-items-center rounded-full bg-secondary text-xs font-bold text-white">{initial}</span>
        <span data-testid="account-email" className="hidden max-w-[10rem] truncate text-sm font-medium text-slate-700 sm:block">{display}</span>
        <ChevronDown className="hidden h-4 w-4 text-slate-400 sm:block" />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 z-30 mt-1.5 w-60 rounded-lg border border-slate-200 bg-white p-1 shadow-lg" data-testid="user-menu-panel">
          <div className="px-3 py-2">
            {me.user.name && <p className="truncate text-sm font-semibold text-slate-900">{me.user.name}</p>}
            <p className="truncate text-xs text-slate-500">{email}</p>
            {me.user.platform_role === "platform_admin" && (
              <p className="mt-1 inline-flex items-center gap-1 rounded bg-accent px-1.5 py-0.5 text-[10px] font-semibold text-primary">Platform Administrator</p>
            )}
          </div>
          <div className="my-1 h-px bg-slate-100" />
          <button
            role="menuitem"
            onClick={() => { onNavigate("account"); setOpen(false); }}
            data-testid="menu-account"
            aria-current={activeView === "account" ? "page" : undefined}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100"
          >
            <CircleUserRound className="h-4 w-4 text-slate-400" /> Account
          </button>
          <button
            role="menuitem"
            onClick={onSignOut}
            data-testid="sign-out-button"
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100"
          >
            <LogOut className="h-4 w-4 text-slate-400" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
};

export default function AppShell({ me, memberships, active, activeView, onNavigate, onSwitch, onSignOut, api, onCreateOrg, children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const isPlatformAdmin = me?.user?.platform_role === "platform_admin";
  const canCreate = !!me?.user?.can_create_organizations;
  const atLimit = !!me?.org_creation?.at_limit;
  const inAdmin = activeView === "admin";

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar (desktop) */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-30 hidden flex-col border-r border-slate-200 bg-white transition-[width] md:flex",
          collapsed ? "w-16" : "w-64"
        )}
      >
        <div className={cn("flex h-16 items-center border-b border-slate-200", collapsed ? "justify-center px-0" : "px-4")}>
          {collapsed ? <BrandMark size={28} /> : <LogoLockup />}
        </div>
        <div className={cn("flex-1 overflow-y-auto py-4", collapsed ? "px-2" : "px-3")}>
          <NavSections isPlatformAdmin={isPlatformAdmin} activeView={activeView} onNavigate={onNavigate} collapsed={collapsed} />
        </div>
        <div className={cn("border-t border-slate-200 p-3", collapsed && "px-2")}>
          <button
            onClick={() => setCollapsed((v) => !v)}
            data-testid="sidebar-collapse-toggle"
            className={cn("flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900", collapsed && "justify-center px-0")}
            title={collapsed ? "Expand" : "Collapse"}
          >
            {collapsed ? <PanelLeft className="h-[18px] w-[18px]" /> : <><PanelLeftClose className="h-[18px] w-[18px]" /> Collapse</>}
          </button>
        </div>
      </aside>

      {/* Mobile nav drawer */}
      {mobileNav && (
        <div className="fixed inset-0 z-40 md:hidden" data-testid="mobile-app-nav">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setMobileNav(false)} />
          <div className="absolute inset-y-0 left-0 flex w-64 flex-col bg-white shadow-xl">
            <div className="flex h-16 items-center justify-between border-b border-slate-200 px-4">
              <LogoLockup />
              <button className="grid h-9 w-9 place-items-center rounded-md hover:bg-slate-100" onClick={() => setMobileNav(false)} aria-label="Close menu">
                <X className="h-5 w-5 text-slate-600" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              <NavSections isPlatformAdmin={isPlatformAdmin} activeView={activeView} onNavigate={(k) => { onNavigate(k); setMobileNav(false); }} collapsed={false} />
            </div>
          </div>
        </div>
      )}

      {/* Main column */}
      <div className={cn("flex min-h-screen flex-col transition-[padding] md:pl-64", collapsed && "md:pl-16")}>
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur-md sm:px-6">
          <div className="flex items-center gap-3">
            <button className="grid h-10 w-10 place-items-center rounded-md text-slate-600 hover:bg-slate-100 md:hidden" onClick={() => setMobileNav(true)} aria-label="Open menu" data-testid="open-mobile-nav">
              <Menu className="h-5 w-5" />
            </button>
            {inAdmin ? (
              <span className="flex items-center gap-2 rounded-lg border border-primary/20 bg-accent px-3 py-1.5 text-sm font-semibold text-slate-900" data-testid="platform-context-indicator">
                <ShieldCheck className="h-4 w-4 text-primary" /> RedVolt Administration
              </span>
            ) : (
              <OrgSwitcher memberships={memberships} active={active} onSwitch={onSwitch}
                canCreate={canCreate} atLimit={atLimit} api={api} onCreateOrg={onCreateOrg} />
            )}
          </div>
          <UserMenu me={me} activeView={activeView} onNavigate={onNavigate} onSignOut={onSignOut} />
        </header>
        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <div className="mx-auto w-full max-w-6xl">{children}</div>
        </main>
      </div>
    </div>
  );
}
